import { inject, Injectable } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import type { Subscription } from 'rxjs';

import type { Alert } from '../models/alert';
import type { ApiResponse } from '../models/api-response';
import { AlertService } from '../services/alert.service';
import { ApiService } from '../services/api.service';
import { toCamelCase } from './utitlities/case-conversion';

@Injectable()
export class GlobalFormBase<T = Record<string, unknown>> {
  router = inject(Router);
  _route = inject(ActivatedRoute);
  alertService = inject(AlertService);
  apiService = inject(ApiService);
  formBuilder = inject(FormBuilder);

  form!: FormGroup;
  sub!: Subscription;
  id: string | null = null;
  detailApi!: string;
  formDetail: T | null = null;
  formType = '';
  onlyImage = false;
  hasFile = false;
  responseData: T | null = null;
  apiSlug!: string;
  formObj: Partial<T> | null = null;
  currentDate = new Date();
  listUrl = '';
  isSuccessMsg = true;

  initBase(): void {
    this.form = this.formBuilder.group({});
    this.sub = this._route.params.subscribe((params) => {
      this.id = params['id'];
      this.formType = params['type'];
      this.checkFormType();
    });
  }

  checkFormType(): void {
    this.onlyImage = this.formType === 'view';
  }

  validateFormType(): void {
    if (this.formType && !['add', 'edit', 'view'].includes(this.formType)) {
      this.redirectToNotFoundPage();
    }
  }

  redirectToNotFoundPage(): void {
    this.router.navigateByUrl('main/404');
  }

  onLocationBack(): void {
    window.history.back();
  }

  onSubmit(): void {
    this.beforeSubmit();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const ALERT_DATA: Alert = {
        heading: 'WARNING',
        message: 'Please fill all the required fields.',
        matIcon: 'close',
      };
      this.alertService.error(ALERT_DATA);
      return;
    }

    const FORM_DATA = (this.formObj as Partial<T>) || this.form.value;

    this.apiService.post<T>(this.apiSlug, FORM_DATA, false, false, this.hasFile).subscribe({
      next: (response: ApiResponse<T>) => {
        if (response.statusCode === 200 && response.data) {
          if (this.isSuccessMsg) {
            this.alertService.success({
              heading: 'Success',
              message: response.message || '',
            });
          }

          this.responseData = response.data;

          if (this.listUrl) {
            this.router.navigateByUrl(this.listUrl);
          }

          this.afterSuccessfullyAdd(response.data);
        } else {
          this.alertService.error({
            heading: 'Error',
            message: response.message || '',
          });
        }
      },
      error: (err) => {
        this.alertService.error({
          heading: 'Error',
          message: err.message || 'Something went wrong',
        });
      },
    });
  }

  beforeSubmit(): void {
    // optional override
  }

  afterSuccessfullyAdd(_response?: T): void {
    // optional override
  }

  onClickActionRoute(url: string): void {
    this.router.navigateByUrl(url);
  }

  getDetail(): void {
    this.apiService.get<T>(this.detailApi).subscribe({
      next: (result: ApiResponse<T>) => {
        if (result.statusCode === 200 && result.data) {
          this.formDetail = result.data;
          if (this.form && typeof this.form.patchValue === 'function') {
            this.form.patchValue(result.data);
          }
          this.afterDetail();
        } else {
          this.formDetail = null;
          this.alertService.error({
            heading: 'Error',
            message: result.message || '',
          });
        }
      },
      error: (err) => {
        this.formDetail = null;
        this.alertService.error({
          heading: 'Error',
          message: err.message || '',
        });
      },
    });
  }

  getDetailPost<P extends Record<string, unknown> | FormData = Partial<T>>(payload: P): void {
    this.apiService.post<T>(this.detailApi, payload).subscribe({
      next: (result: ApiResponse<T>) => {
        if (result.statusCode === 200 && result.data) {
          this.formDetail = toCamelCase(result.data) as T;
          if (this.form && typeof this.form.patchValue === 'function') {
            this.form.patchValue(result.data);
          }
          this.afterDetail();
        } else {
          this.formDetail = null;
          this.alertService.error({
            heading: 'Error',
            message: result.message || '',
          });
        }
      },
      error: (err) => {
        this.formDetail = null;
        this.alertService.error({
          heading: 'Error',
          message: err.message || '',
        });
      },
    });
  }

  afterDetail(): void {
    // optional override
  }

  findInvalidControls(control: string, form?: FormGroup): boolean {
    const TARGETED_FORM = form || this.form;
    return (
      TARGETED_FORM.get(control)?.invalid === true || TARGETED_FORM.get(control)?.touched === true
    );
  }

  createFormDataDynamically(obj: Partial<T>): FormData {
    const FORM_DATA = new FormData();

    Object.entries(obj).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        return;
      }
      if (value instanceof Blob) {
        FORM_DATA.append(key, value);
      } else {
        FORM_DATA.append(key, String(value));
      }
    });

    return FORM_DATA;
  }
}
